import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import GraphHeader from "../../CommonComponents/GraphHeader";
import FecthingPage from "../../CommonComponents/FecthingPage";
import {
  ProductivityTrackerThunk,
  resetError,
} from "../../../../../redux/features/Settings/Statistics";
import CommonToast from "../../../../_utils/CommonToast";
import ICONS from "../../../../../assets/Icons";
import {
  errorcode,
  GlobalConfiguration,
} from "../../../../../utils/configurationProvider";
import globalText from "../../../../../utils/globalText";
import { toCamelCase } from "../../../../../utils/toCamelCase";

/**
 * ProductivityTracker - A component that displays employee productivity statistics
 * in a tabular format with transaction created/approved counts. Supports filtering by
 * time period, transaction type, and employee selection.
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.toggleAddedToHome - Callback to toggle widget on home dashboard
 * @param {boolean} props.isAddedToHome - Whether this widget is added to home dashboard
 * @param {boolean} [props.autoFetch=false] - Whether to fetch data automatically on mount
 * @param {string} props.flow - Display flow type: 'homeDashboard' or other
 * @returns {JSX.Element} Rendered productivity tracker component
 */
const ProductivityTracker = ({
  toggleAddedToHome,
  isAddedToHome,
  autoFetch = false,
  flow,
}) => {
  // Display data state for table rows and totals
  // Initialized with placeholder rows based on GlobalConfiguration.lengthCheckFour (4 rows)
  const [displayData, setDisplayData] = useState({
    tableRows: Array.from({ length: GlobalConfiguration?.lengthCheckFour }).map(
      (_, idx) => ({
        id: idx,
        name: "-",
        userId: "-",
        created: "-",
        approved: "-",
      }),
    ),
    totalCreated: "-",
    totalApproved: "-",
  });

  // Time period filter state (defaults to last 7 days)
  const [selectedDays, setSelectedDays] = useState(
    globalText?.LAST_7_DAYS.toLowerCase(),
  );

  const homeDashboard = toCamelCase(globalText?.HOME_DASHBOARD);
  const fieldForce = toCamelCase(globalText?.FIELD_FORCE);
  const fieldForceTxnDashboard =
    fieldForce + globalText?.TRANSACTION + globalText?.DASHBOARD;

  const DASHBOARD_FLOWS = [homeDashboard, fieldForceTxnDashboard];
  // Transaction type filter: 'bene', 'fieldForce', 'reimbursement', 'internal'
  const [selectedTxnOption, setSelectedTxnOption] = useState(
    flow === fieldForceTxnDashboard
      ? fieldForce
      : globalText?.BENEFICIARY_OPTION.toLowerCase(),
  );

  // Selected employee(s) for filtering - can be single or multiple
  const [selectedEmployee, setSelectedEmployee] = useState([]);

  // Controls whether the fetch page UI is shown (vs auto-fetched data)
  const [fetchPageEnabled, setFetchPageEnabled] = useState(!autoFetch);

  // Error state for displaying error UI
  const [isError, setIsError] = useState(false);

  const toastShownRef = useRef(false);

  // Loading state for manual fetch button
  const [fetchPageLoading, setFetchPagefetchPageLoading] = useState(false);

  // Check if user has access to global statistics service
  const services = useSelector((state) => state.refreshToken.services);
  const globalStatisticsService = services?.includes(
    globalText?.VIEW_STATISTICS,
  );

  const dispatch = useDispatch();
  const loginData = useSelector((state) => state.refreshToken.data);

  // Select loading and error states from Redux
  const { ProductivityTrackerThunkLoading, ProductivityTrackerThunkError } =
    useSelector((state) => state.settingStatistics);

  // User data loading state (for auto-fetch dependency)
  const userDataLoading = useSelector((state) => state.users.isLoading);

  // Extract entity ID from login data if user is authenticated
  const entityId = loginData?.status ? loginData?.entity?.entity_id : "";

  /* Fetch and update state with API data */
  const handleCalculation = useCallback(
    async (response) => {
      const reportData = response?.payload?.report_data;

      if (response?.payload?.status && reportData) {
        // Map productivity tracker array to display rows, or use placeholders if empty
        const rows = reportData.productivity_tracker?.length
          ? reportData.productivity_tracker.map((emp, idx) => ({
              id: idx,
              name: emp.user_full_name || "-",
              userId: emp.user_id || "-",
              created: emp.transaction_created_count ?? "-",
              approved: emp.transaction_approved_count ?? "-",
            }))
          : Array.from({ length: GlobalConfiguration?.lengthCheckFour }).map(
              (_, idx) => ({
                id: idx,
                name: "-",
                userId: "-",
                created: "-",
                approved: "-",
              }),
            );

        // Update display data with mapped rows and totals
        setDisplayData({
          tableRows: rows,
          totalCreated: reportData.total_transaction_created_count ?? "-",
          totalApproved: reportData.total_transaction_approved_count ?? "-",
        });

        // Show success toast only once per fetch cycle
        if (!toastShownRef.current && fetchPageEnabled) {
          CommonToast({
            text: globalText?.FETCHED_SUCCESSFULLY, //"Fetched successfully.",
            icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
            variant: "var(--success)",
            btnProps: {
              isDisabled: false,
              enabledTextColor: "var(--text-primary)",
              enabledBgColor: "var(--primary-bg)",
            },
          });
          toastShownRef.current = true;
        }
        setFetchPageEnabled(false);
        setIsError(false);
      } else {
        // fallback to defaults
        // Enable fetch page unless it's a "not found" error (no data exists)
        setFetchPageEnabled(
          response?.payload?.status !== errorcode?.errorNotFound,
        );
        toastShownRef.current = false;
        setDisplayData({
          tableRows: Array.from({
            length: GlobalConfiguration?.lengthCheckFour,
          }).map((_, idx) => ({
            id: idx,
            name: "-",
            userId: "-",
            created: "-",
            approved: "-",
          })),
          totalCreated: "-",
          totalApproved: "-",
        });
        // setIsError(true); // Temporarily disabled — batching causes partial failures that
        // falsely trigger this error. Backend fix pending -- will re-enable after optimization.
      }
    },
    [dispatch, entityId, selectedDays, selectedTxnOption, selectedEmployee],
  );

  /* Fetch and update state with API data */
  const fetchData = async () => {
    // Guard: no entity ID
    if (!entityId) return;

    setFetchPagefetchPageLoading(true);

    // Normalize selectedEmployee to an array of IDs
    const employeeIds = Array.isArray(selectedEmployee)
      ? selectedEmployee
      : selectedEmployee
        ? [selectedEmployee]
        : [];

    // Check if we have valid employee data to fetch
    const hasEmployeeData = Array.isArray(selectedEmployee)
      ? selectedEmployee.length > GlobalConfiguration.lengthCheckZero
      : Boolean(selectedEmployee?.value ?? selectedEmployee);

    if (!hasEmployeeData) return;

    /**
     *  Note: Sending all user IDs in a single API request may result in a 414 (URL Too Long) error. Therefore, we are currently sending the requests in batches of 100.
     *  The API team is expected to update this endpoint to use a POST request in the future [Ticket number: #47410]. We will incorporate the changes once we receive further updates.
     */
    const chunkSize = 100;
    let chunks = [];
    if (employeeIds.length > GlobalConfiguration.lengthCheckZero) {
      for (
        let i = GlobalConfiguration.lengthCheckZero;
        i < employeeIds.length;
        i += chunkSize
      ) {
        chunks.push(employeeIds.slice(i, i + chunkSize));
      }
    } else {
      chunks.push(undefined);
    }
    try {
      const promises = chunks.map((chunk) =>
        dispatch(
          ProductivityTrackerThunk({
            user_ids: chunk,
            period: selectedDays,
            entity_id: entityId,
            reportType:
              selectedTxnOption === globalText?.BENEFICIARY_OPTION.toLowerCase() //"bene"
                ? globalText?.BENEFICIARY_OPTION.toLowerCase()
                : selectedTxnOption === fieldForce
                  ? "field_force"
                  : selectedTxnOption ===
                      globalText?.REIMBURSEMENT.toLowerCase()
                    ? globalText?.REIMBURSEMENT.toLowerCase() //"reimbursement"
                    : globalText?.INTERNAL.toLowerCase(),
          }),
        ),
      );

      const responses = await Promise.all(promises);

      // Filter to find all successful responses
      const successfulResponses = responses.filter(
        (res) =>
          res &&
          res.type?.endsWith("/fulfilled") &&
          res.payload?.status &&
          res.payload?.report_data,
      );

      if (successfulResponses.length === GlobalConfiguration.lengthCheckZero) {
        // If all requests failed, find the first failure (or fallback) to trigger the error fallback
        const failedResponse = responses.find((res) => res) || {
          payload: {
            status: false,
            report_data: null,
          },
        };
        setFetchPagefetchPageLoading(false);
        handleCalculation(failedResponse);
        return;
      }

      // Combine productivity_tracker list and sum counts from successful responses
      let combinedTracker = [];
      let totalCreated = GlobalConfiguration.lengthCheckZero;
      let totalApproved = GlobalConfiguration.lengthCheckZero;

      successfulResponses.forEach((res) => {
        const reportData = res.payload.report_data;
        if (Array.isArray(reportData.productivity_tracker)) {
          combinedTracker = combinedTracker.concat(
            reportData.productivity_tracker,
          );
        }
        totalCreated +=
          Number(reportData.total_transaction_created_count) ||
          GlobalConfiguration?.lengthCheckZero;
        totalApproved +=
          Number(reportData.total_transaction_approved_count) ||
          GlobalConfiguration?.lengthCheckZero;
      });

      // Create combined response object matching API structure
      const combinedResponse = {
        payload: {
          status: true,
          report_data: {
            productivity_tracker: combinedTracker,
            total_transaction_created_count: totalCreated,
            total_transaction_approved_count: totalApproved,
          },
        },
      };

      // Sort combined tracker by created count (desc), then approved count (desc)
      combinedResponse.payload.report_data.productivity_tracker =
        combinedResponse.payload.report_data.productivity_tracker.sort(
          (a, b) =>
            b.transaction_created_count - a.transaction_created_count ||
            b.transaction_approved_count - a.transaction_approved_count,
        );

      setFetchPagefetchPageLoading(false);
      handleCalculation(combinedResponse);
    } catch (error) {
      // Handle unexpected errors (network, parsing, etc.)
      console.error("Error fetching productivity tracker:", error);
      setFetchPagefetchPageLoading(false);
      handleCalculation({
        payload: {
          status: false,
          report_data: null,
        },
      });
    }
  };

  /**
   * Handler for manual "Fetch Data" button click.
   * Initiates data fetch by enabling loading and disabling fetch page.
   */
  const handleFetchClick = () => {
    setFetchPagefetchPageLoading(true);
    setFetchPageEnabled(false);
  };

  /**
   * Effect: Trigger data fetch when autoFetch is enabled or fetch page is disabled.
   * Also cleans up Redux errors on unmount.
   * Dependencies include all filter states to re-fetch when filters change.
   */
  useEffect(() => {
    if (autoFetch || !fetchPageEnabled) {
      fetchData();
    }
    return () => {
      dispatch(resetError());
    };
  }, [
    autoFetch,
    fetchPageEnabled,
    selectedDays,
    selectedEmployee,
    selectedTxnOption,
  ]);

  return (
    <div
      className={`w-width-full flex flex-col bg-primary-card ${DASHBOARD_FLOWS?.includes(flow) ? "rounded-b-border-radius-md" : "rounded-border-radius-md"}`}
    >
      {/* Header with title, period filter, transaction type filter, employee filter, and add-to-home toggle */}
      <GraphHeader
        handlingOptionsRequired={[
          {
            type: `${globalText?.DAY.toLowerCase()}s`,
            setterState: setSelectedDays,
          },
          {
            type: toCamelCase(globalText?.TRANSACTION_OPTION), //"txnOption"
            setterState: setSelectedTxnOption,
          },
          {
            type: globalText?.EMPLOYEES.toLowerCase(), //employees
            setterState: setSelectedEmployee,
          },
        ]}
        titleText={globalText?.PRODUCTIVITY_TRACKER}
        isAddedToHome={isAddedToHome}
        toggleAddedToHome={toggleAddedToHome}
        autoFetch={autoFetch}
        hideTitle={DASHBOARD_FLOWS?.includes(flow)}
        flow={flow}
      />
      <div className="flex flex-col relative">
        {/* Fetch/Loading/Error state page */}
        <FecthingPage
          fetchPageEnabled={
            (!autoFetch && fetchPageEnabled) || !globalStatisticsService
          }
          loading={
            ProductivityTrackerThunkLoading ||
            fetchPageLoading ||
            (autoFetch && userDataLoading)
          }
          isError={isError}
          errorData={isError ? ProductivityTrackerThunkError : null}
          handleFunction={handleFetchClick}
        />

        {/* Data Table Container */}
        <div className="w-width-full">
          <div className="h-[var(--statistic-non-graph-height)] w-width-full bg-primary-card overflow-y-auto">
            {/* Desktop Table Header */}
            <div className="hidden Qsmd:flex font-sans text-font-xs font-normal text-text-secondary bg-primary-nav">
              <div className="flex-1 py-padding-sm">
                <span className="Qsmd:pl-padding-lg pl-padding-md Qsm:pr-padding-sm flex Qsm:justify-start justify-center items-center">
                  Employee’s name and user ID
                </span>
              </div>
              <div className="flex-1 text-center py-padding-sm border-r border-l border-primary-card">
                Transactions created
              </div>
              <div className="flex-1 text-center py-padding-sm">
                Transactions approved
              </div>
            </div>

            {/* Table Rows - Map over displayData */}
            {displayData.tableRows.map((row) => (
              <div key={row.id}>
                {/* Desktop View Row */}
                <div className="hidden Qsmd:flex items-center justify-center font-sans font-normal text-text-secondary bg-primary-card py-padding-sm">
                  <div className="flex-1">
                    <div className="text-font-xs Qsmd:pl-padding-lg pl-padding-md capitalize">
                      {row.name}
                    </div>
                    <div className="text-font-xxs Qsmd:pl-padding-lg pl-padding-md">
                      {row.userId}
                    </div>
                  </div>
                  <div className="flex-1 text-font-xs text-center py-padding-sm border-r border-l border-primary-bg">
                    {row.created}
                  </div>
                  <div className="flex-1 text-font-xs text-center py-padding-sm">
                    {row.approved}
                  </div>
                </div>

                {/* Responsive(webApp) View Row */}
                <div className="flex Qsmd:hidden flex-col font-sans font-normal text-text-secondary bg-primary-card px-padding-md py-padding-sm border-b border-primary-bg gap-gap-sm">
                  {/* Row A: Name and User ID */}
                  <div className="flex justify-between items-start w-width-full ">
                    <div className="flex flex-col gap-[var(--mobile-padding-xs)]">
                      <div className="text-font-sm font-normal text-text-primary capitalize">
                        {row.name}
                      </div>
                      <div className="text-font-xxs text-text-secondary">
                        Employee’s name
                      </div>
                    </div>
                    <div className="flex flex-col gap-[var(--mobile-padding-xs)] text-right">
                      <div className="text-font-sm font-normal text-text-primary">
                        {row.userId}
                      </div>
                      <div className="text-font-xxs text-text-secondary">
                        User ID
                      </div>
                    </div>
                  </div>

                  {/* Row B: Transactions Created and Approved */}
                  <div className="flex justify-between items-start w-width-full mt-margin-xs">
                    <div className="flex flex-col gap-[var(--mobile-padding-xs)]">
                      <div className="text-font-xs font-semibold text-text-primary">
                        {row.created}
                      </div>
                      <div className="text-font-xxs text-text-secondary">
                        Transactions created
                      </div>
                    </div>
                    <div className="flex flex-col gap-[var(--mobile-padding-xs)] text-right">
                      <div className="text-font-xs font-semibold text-text-primary">
                        {row.approved}
                      </div>
                      <div className="text-font-xxs text-text-secondary">
                        Transactions approved
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer with Totals */}
        <div className="flex w-width-full bg-primary-nav rounded-b-border-radius-md">
          {/* Desktop View Bottom Row */}
          <div className="hidden Qsmd:flex items-center justify-center py-padding-sm flex-1 flex-row gap-gap-base w-width-full">
            <div className="flex-1 flex-shrink-0 flex"></div>
            <div className="flex-1 flex-shrink-0 flex items-center justify-center border-r border-primary-bg gap-gap-md px-padding-md">
              <div className="text-text-secondary self-end text-font-xxs">
                Total transactions created :
              </div>
              <div className="text-text-primary text-font-xs font-semibold">
                {displayData.totalCreated}
              </div>
            </div>
            <div className="flex-1 flex-shrink-0 flex items-center justify-center gap-gap-md px-padding-md">
              <div className="text-text-secondary self-end text-font-xxs">
                Total transactions approved :
              </div>
              <div className="text-text-primary text-font-xs font-semibold">
                {displayData.totalApproved}
              </div>
            </div>
          </div>

          {/* Responsive (webApp) View Bottom Row */}
          <div className="flex Qsmd:hidden flex-col w-width-full py-padding-sm">
            <div className="flex justify-between items-center w-width-full border-b border-primary-bg pb-padding-sm px-padding-md">
              <div className="text-text-secondary text-font-xs">
                Total transactions created
              </div>
              <div className="text-font-xs font-semibold text-text-primary">
                {displayData.totalCreated}
              </div>
            </div>
            <div className="flex justify-between items-center w-width-full pt-padding-sm px-padding-md">
              <div className="text-text-secondary text-font-xs">
                Total transactions approved
              </div>
              <div className="text-font-xs font-semibold text-text-primary">
                {displayData.totalApproved}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductivityTracker;
